import { FC, useEffect, useRef } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router';

import { AccountsRouter } from '../../accounts/routers/accounts.router.tsx';
import { AuthRouter } from '../../accounts/routers/auth.router.tsx';
import { CommonCompatibilityCheck } from '../../common/components/common-compatibility-check.tsx';
import { CommonRecoveryFallback } from '../../common/components/common-recovery-fallback.tsx';
import { CommonSuspenseFallback } from '../../common/components/common-suspense-fallback.tsx';
import { RootScreenProtector } from '../../common/components/screen-protectors/root-screen-protector.tsx';
import { LearnRouter } from '../../learn/routers/learn.router.tsx';

import { useAppDispatch } from '../hooks/use-app-dispatch.ts';
import { useAppSelector } from '../hooks/use-app-selector.ts';
import { NotFoundScreen } from '../screens/not-found.screen.tsx';
import { loadAppThunk } from '../slices/app-lifecycle/thunks/load-app.thunk.ts';

// Need to export below so we have access to implement navigation without refreshing the app.
// Simply window.location.href will never work.

// eslint-disable-next-line react-refresh/only-export-components
export const browserRouter = createBrowserRouter([
  {
    errorElement: <CommonRecoveryFallback />,
    children: [
      {
        path: '/',
        element: <RootScreenProtector />,
      },
      ...LearnRouter,
      ...AuthRouter,
      ...AccountsRouter,
      {
        path: '*',
        element: <NotFoundScreen />,
      },
    ],
  },
]);

export const MainRouter: FC = () => {
  const dispatch = useAppDispatch();
  const isAppInitialized = useAppSelector((state) => state.appLifecycle.isAppInitialized);
  const effectRef = useRef(false);

  useEffect(() => {
    if (!effectRef.current) {
      effectRef.current = true;
      dispatch(loadAppThunk());
    }
  }, [dispatch]);

  if (!isAppInitialized) {
    return <CommonSuspenseFallback />;
  }

  return (
    <CommonCompatibilityCheck>
      <RouterProvider router={browserRouter} />
    </CommonCompatibilityCheck>
  );
};
